function isObj(v) { return !!v && typeof v === "object" && !Array.isArray(v); }
function isNonEmptyString(v) { return typeof v === "string" && v.trim().length > 0; }
function isFiniteNumber(v) { return typeof v === "number" && Number.isFinite(v); }
function isNonNegativeInt(v) { return Number.isInteger(v) && v >= 0; }

function validateTeam(team, label, mode) {
  if (!isObj(team)) return label + " missing";
  if (!isNonEmptyString(team.name)) return label + ".name missing";
  const minPlayers = mode === "challenge" ? 1 : 2;
  if (!Array.isArray(team.players) || team.players.length < minPlayers) return label + `.players needs at least ${minPlayers} players`;
  if (!team.players.every(isNonEmptyString)) return label + ".players contains invalid names";
  return null;
}

function validateInnings(inn, idx) {
  if (!isObj(inn)) return "innings[" + idx + "] missing";
  if (inn.battingTeam !== "A" && inn.battingTeam !== "B") return "innings[" + idx + "].battingTeam invalid";
  if (!Array.isArray(inn.batsmen) || inn.batsmen.length < 1) return "innings[" + idx + "].batsmen invalid";
  if (!Array.isArray(inn.bowlers) || inn.bowlers.length < 1) return "innings[" + idx + "].bowlers invalid";
  if (!isNonNegativeInt(inn.totalRuns)) return "innings[" + idx + "].totalRuns invalid";
  if (!isNonNegativeInt(inn.wickets)) return "innings[" + idx + "].wickets invalid";
  if (!isNonNegativeInt(inn.balls)) return "innings[" + idx + "].balls invalid";
  const batterNames = new Set();
  for (const batter of inn.batsmen) {
    if (!isObj(batter) || !isNonEmptyString(batter.name) || batterNames.has(batter.name)) return "innings[" + idx + "].batsmen invalid";
    batterNames.add(batter.name);
    for (const key of ["runs", "balls", "fours", "sixes"]) if (!isNonNegativeInt(batter[key])) return "innings[" + idx + "].batsmen." + key + " invalid";
  }
  const bowlerNames = new Set();
  for (const bowler of inn.bowlers) {
    if (!isObj(bowler) || !isNonEmptyString(bowler.name) || bowlerNames.has(bowler.name)) return "innings[" + idx + "].bowlers invalid";
    bowlerNames.add(bowler.name);
    for (const key of ["balls", "runs", "wickets"]) if (!isNonNegativeInt(bowler[key])) return "innings[" + idx + "].bowlers." + key + " invalid";
  }
  if (inn.currentStriker != null && !batterNames.has(inn.currentStriker)) return "innings[" + idx + "].currentStriker invalid";
  if (inn.currentNonStriker != null && !batterNames.has(inn.currentNonStriker)) return "innings[" + idx + "].currentNonStriker invalid";
  if (inn.currentStriker && inn.currentStriker === inn.currentNonStriker) return "innings[" + idx + "].current batters duplicate";
  if (inn.currentBowler != null && !bowlerNames.has(inn.currentBowler)) return "innings[" + idx + "].currentBowler invalid";
  if (!isObj(inn.extras)) return "innings[" + idx + "].extras missing";
  for (const k of ["wide", "nb", "b", "lb"]) {
    if (!isNonNegativeInt(inn.extras[k])) return "innings[" + idx + "].extras." + k + " invalid";
  }
  if (!Array.isArray(inn.ballLog)) return "innings[" + idx + "].ballLog missing";
  for (const ball of inn.ballLog) {
    if (!isObj(ball) || !isNonNegativeInt(ball.runs) || typeof ball.isLegal !== "boolean") return "innings[" + idx + "].ballLog invalid";
    if (!isNonEmptyString(ball.type)) return "innings[" + idx + "].ballLog.type invalid";
  }
  if (inn.wickets > inn.batsmen.length) return "innings[" + idx + "].wickets invalid";
  return null;
}

/**
 * Normalize a raw match snapshot so that legacy / slightly-corrupted data
 * can survive validation.  This is intentionally lenient — it maps old
 * property names, fills missing required fields with sane defaults, and
 * coerces number-like strings.  The result may still fail validation if
 * the damage is too deep, but we rescue everything we can.
 */
export function normalizeSnapshot(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
  const m = JSON.parse(JSON.stringify(raw));

  // --- Top-level team objects --------------------------------------------------
  for (const slot of ["teamA", "teamB"]) {
    if (m[slot] && Array.isArray(m[slot].players)) {
      m[slot].players = m[slot].players.filter(p => typeof p === "string" && p.trim().length > 0);
    }
  }

  // --- Innings normalization ---------------------------------------------------
  if (Array.isArray(m.innings)) {
    for (const inn of m.innings) {
      if (!inn || typeof inn !== "object") continue;

      // Legacy property: "team" → "battingTeam"
      if (inn.battingTeam === undefined && inn.team !== undefined) {
        inn.battingTeam = inn.team;
        delete inn.team;
      }

      // Coerce numeric fields that could be NaN/undefined
      for (const k of ["totalRuns", "wickets", "balls"]) {
        if (typeof inn[k] !== "number" || !Number.isFinite(inn[k])) inn[k] = Number(inn[k]) || 0;
        inn[k] = Math.max(0, Math.round(inn[k]));
      }

      // Ensure extras object with all required keys
      if (!inn.extras || typeof inn.extras !== "object") inn.extras = {};
      for (const ek of ["wide", "nb", "b", "lb"]) {
        if (typeof inn.extras[ek] !== "number" || !Number.isFinite(inn.extras[ek])) inn.extras[ek] = Number(inn.extras[ek]) || 0;
        inn.extras[ek] = Math.max(0, Math.round(inn.extras[ek]));
      }

      // Ensure ballLog is an array and each entry has required fields
      if (!Array.isArray(inn.ballLog)) inn.ballLog = [];
      for (const ball of inn.ballLog) {
        if (!ball || typeof ball !== "object") continue;
        if (typeof ball.runs !== "number" || !Number.isFinite(ball.runs)) ball.runs = Number(ball.runs) || 0;
        ball.runs = Math.max(0, Math.round(ball.runs));
        if (typeof ball.isLegal !== "boolean") ball.isLegal = ball.isLegal !== false && ball.isLegal !== 0;
        if (typeof ball.type !== "string" || ball.type.trim().length === 0) {
          ball.type = ball.runs === 0 ? "dot" : "run";
        }
      }

      // Ensure batsmen / bowlers arrays exist
      if (!Array.isArray(inn.batsmen)) inn.batsmen = [];
      for (const bat of inn.batsmen) {
        if (!bat || typeof bat !== "object") continue;
        for (const k of ["runs", "balls", "fours", "sixes"]) {
          if (typeof bat[k] !== "number" || !Number.isFinite(bat[k])) bat[k] = Number(bat[k]) || 0;
          bat[k] = Math.max(0, Math.round(bat[k]));
        }
      }
      if (!Array.isArray(inn.bowlers)) inn.bowlers = [];
      for (const bwl of inn.bowlers) {
        if (!bwl || typeof bwl !== "object") continue;
        for (const k of ["balls", "runs", "wickets"]) {
          if (typeof bwl[k] !== "number" || !Number.isFinite(bwl[k])) bwl[k] = Number(bwl[k]) || 0;
          bwl[k] = Math.max(0, Math.round(bwl[k]));
        }
      }
    }
  }

  return m;
}

export function validateMatchSnapshot(input) {
  if (!isObj(input)) return { ok: false, error: "match is not an object" };
  if (!isNonEmptyString(input.id)) return { ok: false, error: "id missing" };
  const teamAError = validateTeam(input.teamA, "teamA", input.mode);
  if (teamAError) return { ok: false, error: teamAError };
  const teamBError = validateTeam(input.teamB, "teamB", input.mode);
  if (teamBError) return { ok: false, error: teamBError };
  if (!isFiniteNumber(input.overs) || input.overs <= 0 || input.overs > 50) return { ok: false, error: "overs invalid" };
  if (input.mode !== undefined && input.mode !== "full" && input.mode !== "single" && input.mode !== "challenge") return { ok: false, error: "mode invalid" };
  if (!Array.isArray(input.innings) || input.innings.length < 1 || input.innings.length > 2) return { ok: false, error: "innings invalid" };
  for (let i = 0; i < input.innings.length; i++) {
    const err = validateInnings(input.innings[i], i);
    if (err) return { ok: false, error: err };
  }
  // Consecutive innings must alternate batting teams (A→B or B→A)
  for (let j = 1; j < input.innings.length; j++) {
    if (input.innings[j - 1].battingTeam === input.innings[j].battingTeam) {
      return { ok: false, error: "innings batting teams duplicate (innings " + (j - 1) + " and " + j + ")" };
    }
  }
  if (input.innings.some(inn => inn.balls > input.overs * 6)) return { ok: false, error: "innings balls exceed match overs" };
  const value = JSON.parse(JSON.stringify(input));
  if (!value.mode) value.mode = "full";
  return { ok: true, value };
}
